import React, { useState, useEffect } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useTranslation } from "react-i18next";
import CharacterCard from "../components/CharacterCard";
import { getPublicCharacters } from "../utils/characterUtils";
import { FaSearch, FaFilter, FaTimes } from "react-icons/fa";

const CharacterBoard = () => {
  const { t } = useTranslation();
  const { user } = useAuthenticator((context) => [context.user]);
  
  const [characters, setCharacters] = useState([]);
  const [filteredCharacters, setFilteredCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); // all, official, community
  const [showFilters, setShowFilters] = useState(false);

  // Load public characters on component mount
  useEffect(() => {
    loadPublicCharacters();
  }, []);

  // Filter characters when search term or filter type changes
  useEffect(() => {
    filterCharacters();
  }, [characters, searchTerm, filterType]);

  const loadPublicCharacters = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const publicCharacters = await getPublicCharacters();
      console.log("Loaded public characters:", publicCharacters);
      
      setCharacters(publicCharacters);
    } catch (err) {
      console.error("Error loading public characters:", err);
      setError("Failed to load characters. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const filterCharacters = () => {
    let filtered = [...characters];

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(character => 
        character.name.toLowerCase().includes(search) ||
        character.description?.toLowerCase().includes(search) ||
        character.short_description?.toLowerCase().includes(search) ||
        character.creator_name?.toLowerCase().includes(search)
      );
    }

    // Apply type filter
    if (filterType === "official") {
      filtered = filtered.filter(character => character.is_builtin);
    } else if (filterType === "community") {
      filtered = filtered.filter(character => !character.is_builtin);
    }

    setFilteredCharacters(filtered);
  };

  const handleCharacterOpen = (character) => {
    console.log("Character opened:", character);
    // This will be handled by the CharacterCard component
    // Could add analytics or other tracking here
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const getFilteredCount = () => {
    const total = characters.length;
    const filtered = filteredCharacters.length;
    const official = characters.filter(c => c.is_builtin).length;
    const community = total - official;

    return { total, filtered, official, community };
  };

  const counts = getFilteredCount();

  if (loading) {
    return (
      <div className="min-h-screen bg-black pt-20 px-4">
        <div className="container mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black pt-20 px-4">
        <div className="container mx-auto">
          <div className="bg-red-900/20 backdrop-blur-xl border border-red-500/30 rounded-2xl p-8 text-center">
            <h2 className="text-xl font-light text-red-300 mb-2">Error Loading Characters</h2>
            <p className="text-red-400 mb-6 font-light">{error}</p>
            <button
              onClick={loadPublicCharacters}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-full font-medium transition-colors duration-200"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/10 to-gray-900 pt-20 px-4">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent mb-6">
            {t('characterBoard.title')}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t('characterBoard.subtitle')} {!user && t('characterBoard.subtitleLoggedOut')}
          </p>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 mb-8 shadow-xl border border-gray-700">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search Input */}
            <div className="relative flex-1 w-full">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={t('characterBoard.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-12 py-4 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                >
                  <FaTimes />
                </button>
              )}
            </div>

            {/* Filter Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-3 px-6 py-4 bg-gray-700/50 border border-gray-600 rounded-xl text-white hover:bg-gray-600/50 transition-all duration-200 hover:border-purple-500"
              >
                <FaFilter />
                <span className="whitespace-nowrap font-medium">
                  {filterType === "all" && t('characterBoard.filters.all')}
                  {filterType === "official" && t('characterBoard.filters.official')}
                  {filterType === "community" && t('characterBoard.filters.community')}
                </span>
              </button>

              {showFilters && (
                <div className="absolute right-0 mt-2 w-48 bg-gray-800/90 backdrop-blur-sm border border-gray-600 rounded-xl shadow-xl z-10">
                  <button
                    onClick={() => {setFilterType("all"); setShowFilters(false);}}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-600/50 transition-colors rounded-t-xl ${filterType === "all" ? "bg-purple-600/50" : ""}`}
                  >
{t('characterBoard.filters.all')} ({counts.total})
                  </button>
                  <button
                    onClick={() => {setFilterType("official"); setShowFilters(false);}}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-600/50 transition-colors ${filterType === "official" ? "bg-purple-600/50" : ""}`}
                  >
{t('characterBoard.filters.official')} ({counts.official})
                  </button>
                  <button
                    onClick={() => {setFilterType("community"); setShowFilters(false);}}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-600/50 transition-colors rounded-b-xl ${filterType === "community" ? "bg-purple-600/50" : ""}`}
                  >
{t('characterBoard.filters.community')} ({counts.community})
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Results Summary */}
          <div className="mt-4 text-gray-400 text-sm">
            Showing {counts.filtered} of {counts.total} characters
            {searchTerm && ` matching "${searchTerm}"`}
          </div>
        </div>

        {/* No Results */}
        {filteredCharacters.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-6">🔍</div>
            <h2 className="text-3xl font-light text-gray-300 mb-4">No Characters Found</h2>
            <p className="text-gray-400 mb-8 font-light">
              {searchTerm 
                ? `No characters match "${searchTerm}". Try adjusting your search.`
                : "No characters available with the current filters."}
            </p>
            {(searchTerm || filterType !== "all") && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  setFilterType("all");
                }}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-3 rounded-full font-medium transition-colors duration-200"
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          /* Character Grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
            {filteredCharacters.map((character) => (
              <CharacterCard
                key={character.character_id}
                character={character}
                onCharacterOpen={handleCharacterOpen}
              />
            ))}
          </div>
        )}

        {/* Footer Info */}
        <div className="text-center py-12 text-gray-400">
          <p>
            Want to create your own character? 
            {user ? (
              <span> Visit your Control Panel to get started!</span>
            ) : (
              <span> Sign in to create and publish your own AI characters!</span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CharacterBoard;